import * as React from "react"
import { Calendar } from "./calendar"
import { Popover, PopoverAnchor, PopoverContent } from "./popover"
import { parseDateInput } from "@/lib/dateUtils"

interface DateFieldEditorProps {
  /** Current text value shown in the input (DD.MM.YYYY format) */
  value: string
  /** Standard input onChange */
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  /** Called when the input loses focus (only if focus leaves the entire component) */
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void
  /** Standard input onKeyDown */
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  /** Called when the user picks a date from the calendar */
  onDateSelect: (date: Date) => void
  placeholder?: string
  inputClassName?: string
  focusTargetAttr?: Record<string, string>
  autoFocus?: boolean
}

/**
 * Date field editor: text input with an auto-opening calendar popover.
 * The calendar opens automatically on mount and is anchored to the input.
 * Selecting a date fires onDateSelect; typing works normally via onChange.
 */
export function DateFieldEditor({
  value,
  onChange,
  onBlur,
  onKeyDown,
  onDateSelect,
  placeholder = "DD.MM.YYYY",
  inputClassName,
  focusTargetAttr,
  autoFocus,
}: DateFieldEditorProps) {
  const [popoverOpen, setPopoverOpen] = React.useState(false)
  const parsedDate = React.useMemo(() => parseDateInput(value), [value])
  // Ref for the input wrapper (lives in the main DOM tree)
  const containerRef = React.useRef<HTMLDivElement>(null)
  // Ref for the popover content (lives in a Radix Portal, separate DOM tree)
  const popoverContentRef = React.useRef<HTMLDivElement>(null)

  // Open popover after mount so the anchor element is in the DOM
  React.useEffect(() => {
    const frame = requestAnimationFrame(() => setPopoverOpen(true))
    return () => cancelAnimationFrame(frame)
  }, [])

  const handleBlur = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const related = e.relatedTarget as HTMLElement | null
      if (related) {
        // Focus moved to the calendar inside the Radix Portal — don't fire blur
        if (popoverContentRef.current?.contains(related)) return
        // Focus moved to something else inside the container — don't fire blur
        if (containerRef.current?.contains(related)) return
      }
      setPopoverOpen(false)
      onBlur(e)
    },
    [onBlur]
  )

  return (
    <div ref={containerRef} className="w-full min-w-0">
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen} modal={false}>
        <PopoverAnchor asChild>
          <input
            type="text"
            value={value}
            onChange={onChange}
            onBlur={handleBlur}
            onKeyDown={onKeyDown}
            className={inputClassName}
            placeholder={placeholder}
            {...focusTargetAttr}
            autoFocus={autoFocus}
          />
        </PopoverAnchor>
        <PopoverContent
          ref={popoverContentRef}
          className="w-auto p-0"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => {
            // Don't close when clicking the input (which is outside the portal)
            if (containerRef.current?.contains(e.target as HTMLElement)) {
              e.preventDefault()
            }
          }}
          onInteractOutside={(e) => {
            if (containerRef.current?.contains(e.target as HTMLElement)) {
              e.preventDefault()
            }
          }}
        >
          <Calendar
            mode="single"
            selected={parsedDate ?? undefined}
            defaultMonth={parsedDate ?? undefined}
            onSelect={(day) => {
              if (day) {
                onDateSelect(day)
                setPopoverOpen(false)
              }
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
